import { DatePipe } from '@angular/common';
import { Component, input, OnDestroy, OnInit } from '@angular/core';
import {
	FormBuilder,
	FormControl,
	FormGroup,
	ReactiveFormsModule,
} from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AsyncSubject, map, Observable, takeUntil, tap } from 'rxjs';
import { IfEmptyPipe } from 'src/lib/pipes/if-empty.pipe';
import {
	StudentSearchArgument,
	StudentSearchModel,
} from 'src/lib/services/api/students/student-search.model';
import { StudentsService } from 'src/lib/services/api/students/students.service';
import {
	EqualityComparerDateComponent,
	EqualityComparerDateValue,
} from 'src/lib/templates/controls/equality-comparer-date/equality-comparer-date.component';
import {
	EqualityComparerNumberInputComponent,
	EqualityComparerNumberInputValue,
} from 'src/lib/templates/controls/equality-comparer-number-input/equality-comparer-number-input.component';
import { YesNoSelectComponent } from 'src/lib/templates/controls/yes-no-select/yes-no-select.component';
import { PaginationTableApi } from 'src/lib/templates/tables/pagination-table/pagination-table-api';
import { PaginationTableConfig } from 'src/lib/templates/tables/pagination-table/pagination-table-config';
import { PaginationTableModule } from 'src/lib/templates/tables/pagination-table/pagination-table.module';
import { PaginationTableId } from 'src/lib/templates/tables/pagination-table/types/pagination-table-id';
import { FormControlWrapper } from 'src/lib/types/forms.def';
import { PaginationTableResultSet } from 'src/lib/types/pagination-table-result-set.def';
import {
	buildSearchFieldDate,
	buildSearchFieldInNotInObject,
	buildSearchFieldLikeObject,
	buildSearchFieldNumber,
} from 'src/lib/utilities/api/patterns/pagination/paging-search-field.argument';
import {
	booleanToSortOrderDescending,
	PagingArgument,
} from 'src/lib/utilities/api/patterns/pagination/paging.argument';
import { SearchOperators } from 'src/lib/utilities/api/patterns/pagination/search-operators.enum';

interface FormGroupModel {
	uid: EqualityComparerNumberInputValue;
	first_name: string;
	last_name: string;
	middle_name: string;
	suffix_name: string;
	maiden_name: string;
	birthdate: EqualityComparerDateValue;
	race: string;
	gender: string;
	account_email: string;
	has_activated_date: boolean;
	institute_title: string;
	program_type: string;
	reported_grade: string;
	state: string;
	currently_enrolled: boolean;
	itype: string;
}

@Component({
	selector: 'ae-assign-students-modal',
	imports: [
		DatePipe,
		EqualityComparerDateComponent,
		EqualityComparerNumberInputComponent,
		IfEmptyPipe,
		PaginationTableModule,
		ReactiveFormsModule,
		YesNoSelectComponent,
	],
	templateUrl: './assign-students-modal.component.html',
	styleUrl: './assign-students-modal.component.scss',
})
export class AssignStudentsModalComponent implements OnInit, OnDestroy {
	private _unsubscribe$ = new AsyncSubject<null>();

	readonly subtitle = input.required<string>();
	readonly programIds = input<number[] | null>();

	public tableConfig: PaginationTableConfig<
		StudentSearchModel,
		FormControlWrapper<FormGroupModel>
	>;
	public tableApi: PaginationTableApi;

	public loading = true;

	constructor(
		public activeModal: NgbActiveModal,
		private studentsService: StudentsService,
		private fb: FormBuilder,
	) {}

	ngOnInit(): void {
		this.initTable();
	}

	ngOnDestroy(): void {
		this._unsubscribe$.next(null);
		this._unsubscribe$.complete();
		this._unsubscribe$ = null;
	}

	private initTable = () => {
		const searchForm = this.fb.group<FormControlWrapper<FormGroupModel>>(
			{
				account_email: new FormControl(null),
				has_activated_date: new FormControl(null),
				birthdate: new FormControl(null),
				first_name: new FormControl(null),
				gender: new FormControl(null),
				institute_title: new FormControl(null),
				last_name: new FormControl(null),
				maiden_name: new FormControl(null),
				middle_name: new FormControl(null),
				program_type: new FormControl(null),
				race: new FormControl(null),
				suffix_name: new FormControl(null),
				uid: new FormControl(null),
				reported_grade: new FormControl(null),
				state: new FormControl(null),
				currently_enrolled: new FormControl(null),
				itype: new FormControl(null),
			},
			{
				updateOn: 'change',
			},
		);

		this.tableConfig = PaginationTableConfig.Create<StudentSearchModel>({
			tableId: PaginationTableId['assign.students.table'],
			getPage$: this.getPage,
			search: {
				form: searchForm,
			},
			controls: {
				refresh: {
					hardRefresh: () => this.tableApi.refresh(),
				},
			},
			pagination: {
				initialSort: { columnId: 'first_name', descending: false },
			},
			selection: { keyProperty: 'uid' },
		});

		this.tableConfig.tableReady$
			.pipe(
				tap((x) => {
					this.tableApi = x;
				}),
				takeUntil(this._unsubscribe$),
			)
			.subscribe();
		this.loading = false;
	};

	private getPage = (
		pageNumber: number,
		itemsPerPage: number,
		sortKey?: string,
		sortDescending?: boolean,
		searchForm?: FormGroup<FormControlWrapper<FormGroupModel>>,
	): Observable<PaginationTableResultSet<StudentSearchModel>> => {
		pageNumber = pageNumber - 1;
		const values = searchForm.value;

		const args: PagingArgument<StudentSearchArgument> = {
			skip: pageNumber * itemsPerPage,
			take: itemsPerPage,
			sort: {
				by: sortKey,
				order: booleanToSortOrderDescending(sortDescending),
			},
			filters: {
				account_email: buildSearchFieldLikeObject(values.account_email),
				has_activated_date:
					values.has_activated_date != null
						? {
								operator: SearchOperators.Equals,
								value: values.has_activated_date ? 1 : 0,
							}
						: null,
				birthdate: buildSearchFieldDate(values.birthdate, 'yyyy-mm-dd'),
				first_name: buildSearchFieldLikeObject(values.first_name),
				gender: buildSearchFieldLikeObject(values.gender),
				institute_id:
					this.programIds() != null
						? buildSearchFieldInNotInObject({
								or: true,
								values: this.programIds(),
							})
						: null,
				institute_title: buildSearchFieldLikeObject(values.institute_title),
				last_name: buildSearchFieldLikeObject(values.last_name),
				maiden_name: buildSearchFieldLikeObject(values.maiden_name),
				middle_name: buildSearchFieldLikeObject(values.middle_name),
				race: buildSearchFieldLikeObject(values.race),
				reported_grade: buildSearchFieldLikeObject(values.reported_grade),
				state: buildSearchFieldLikeObject(values.state),
				suffix_name: buildSearchFieldLikeObject(values.suffix_name),
				uid: buildSearchFieldNumber(values.uid),
				itype: buildSearchFieldLikeObject(values.itype),
				currently_enrolled:
					values.currently_enrolled != null
						? {
								operator: SearchOperators.Equals,
								value: values.currently_enrolled ? 1 : 0,
							}
						: null,
			},
		};

		return this.studentsService.searchStudents(args).pipe(
			map((x) => {
				return {
					items: x.results,
					count: x.count,
				} as PaginationTableResultSet<StudentSearchModel>;
			}),
			takeUntil(this._unsubscribe$),
		);
	};
}
